// اجزای مشترک صفحات INT (AP-INT-02): کلیدهای Query، عنوان Adapter و پنجره سلامت.
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import type { HealthWindow } from '@phoenix/api-client';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, SampleBadge } from '@phoenix/ui';

export const intKeys = {
  endpoints: (tenantId: string | undefined) => ['integration', 'endpoints', tenantId] as const,
  providers: (tenantId: string | undefined) => ['integration', 'providers', tenantId] as const,
  secrets: (tenantId: string | undefined, endpointId: string) => ['integration', 'secrets', tenantId, endpointId] as const,
  health: (tenantId: string | undefined) => ['integration', 'health', tenantId] as const,
  calls: (tenantId: string | undefined) => ['integration', 'calls', tenantId] as const,
  deadLetters: (tenantId: string | undefined, status: string) => ['integration', 'dead-letters', tenantId, status] as const,
  // AP-INT-03 (SCR-STAFF-1003).
  agents: (tenantId: string | undefined) => ['integration', 'agents', tenantId] as const,

  // AP-INT-10 (SCR-STAFF-1006 و SCR-STAFF-1009).
  insurerCredentials: (tenantId: string | undefined) => ['integration', 'insurer-credentials', tenantId] as const,
  insurerSites: (tenantId: string | undefined) => ['integration', 'insurer-sites', tenantId] as const,
  insurerClinics: (tenantId: string | undefined) => ['integration', 'insurer-clinics', tenantId] as const,

  // AP-INT-09 (SCR-STAFF-1006، 1007، 1008 و کارت میز کار).
  providerCredentials: (tenantId: string | undefined) => ['integration', 'provider-credentials', tenantId] as const,
  credentialStatus: (tenantId: string | undefined, branchId: string | undefined) =>
    ['integration', 'credential-status', tenantId, branchId] as const,
  entityCalls: (tenantId: string | undefined, consumerRef: string) =>
    ['integration', 'entity-calls', tenantId, consumerRef] as const,
};

/** عنوان i18n یک Adapter (کد ناشناخته ← خود کد). */
export function useAdapterTitle() {
  const { t } = useTranslation('int');
  return (code: string | null | undefined) => (code ? t(`int:adapter.${code}`, { defaultValue: code }) : '—');
}

/** نام Adapter با نشان «نمونه» برای محتوای IsSample (CLAUDE.md §10). */
export function AdapterName({ code, isSample }: { code: string | null | undefined; isSample: boolean }) {
  const title = useAdapterTitle();
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <span>{title(code)}</span>
      {isSample ? <SampleBadge /> : null}
    </Stack>
  );
}

/** خلاصه یک پنجره سلامت (نرخ موفقیت، میانگین و صدک ۹۵). */
export function HealthWindowText({ value }: { value: HealthWindow }) {
  const { t } = useTranslation('int');
  const calls = Number(value.calls);
  if (calls === 0) {
    return <span>{t('int:health.no_calls')}</span>;
  }
  const rate = value.successRate === null ? 0 : Number(value.successRate);
  return (
    <span>
      {t('int:health.window', {
        rate: faDigits(Math.round(rate * 100)),
        calls: faDigits(calls),
        avg: faDigits(Number(value.avgMs ?? 0)),
        p95: faDigits(Number(value.p95Ms ?? 0)),
      })}
    </span>
  );
}

/** وضعیت مدار. */
export function CircuitChip({ state }: { state: string }) {
  const { t } = useTranslation('int');
  const color = state === 'Closed' ? 'success' : state === 'Open' ? 'error' : 'warning';
  return <Chip size="small" color={color} variant="outlined" label={t(`int:health.circuit.${state}`, { defaultValue: state })} />;
}
